import { VehicleInput, VehicleType } from "../../../generated/nest-graphql";
import { isSavedAsEmpty } from "../emptyText.utils";

export enum ParkingBrake {
  MANUAL = "Manual",
  ELECTRIC = "Electric",
}

// NOT_SET is the picker's own empty choice.
export enum RearDrumBrakes {
  YES = "YES",
  NO = "NO",
  NOT_SET = "",
}

/** What the form edits, all as typed text so a half-typed number is never lost. */
export type EditVehicleValues = {
  year: string;
  make: string;
  model: string;
  subModel: string;
  vin: string;
  licensePlate: string;
  licensePlateState: string;
  odometer: string;
  rearDrumBrakes: RearDrumBrakes;
  parkingBrake: string;
  frontRotorsDiscardThicknessInches: string;
  rearRotorsDiscardThicknessInches: string;
  wheelTorque: string;
  oilCapacityQuarts: string;
  CCA: string;
  notes: string;
};

export type EditVehicleErrors = Partial<Record<keyof EditVehicleValues, string>>;

const text = (value?: string | number | null) => {
  const shown = value === null || value === undefined ? "" : String(value).trim();
  return isSavedAsEmpty(shown) ? "" : shown;
};

export const toFormValues = (vehicle: VehicleType): EditVehicleValues => {
  const info = vehicle.vehicleTechnicalInfo;
  return {
    year: text(vehicle.year),
    make: text(vehicle.make),
    model: text(vehicle.model),
    subModel: text(vehicle.subModel),
    vin: text(vehicle.vin),
    licensePlate: text(vehicle.licensePlate),
    licensePlateState: text(vehicle.licensePlateState),
    odometer: text(vehicle.odometer),
    rearDrumBrakes:
      info?.hasDrumBrakes === true
        ? RearDrumBrakes.YES
        : info?.hasDrumBrakes === false
        ? RearDrumBrakes.NO
        : RearDrumBrakes.NOT_SET,
    parkingBrake: text(info?.parkingBrake),
    frontRotorsDiscardThicknessInches: text(info?.frontRotorsDiscardThicknessInches),
    rearRotorsDiscardThicknessInches: text(info?.rearRotorsDiscardThicknessInches),
    wheelTorque: text(info?.wheelTorque),
    oilCapacityQuarts: text(info?.oilCapacityQuarts),
    CCA: text(vehicle.CCA),
    notes: text(vehicle.notes),
  };
};

const DECIMAL = /^(\d+(\.\d*)?|\.\d+)$/;
const WHOLE = /^\d+$/;
// 17 characters, no I, O or Q, as every VIN since 1981.
const VIN = /^[A-HJ-NPR-Z0-9]{17}$/;

const digitsOnly = (value: string) => value.replace(/[,\s]/g, "");

// Year, make and model are required; the rest only where it differs from the record, so old values never block a save.
export const validate = (values: EditVehicleValues, initial?: EditVehicleValues): EditVehicleErrors => {
  const errors: EditVehicleErrors = {};
  const edited = (field: keyof EditVehicleValues) => !initial || values[field] !== initial[field];
  if (!values.year.trim()) errors.year = "Required";
  if (!values.make.trim()) errors.make = "Required";
  if (!values.model.trim()) errors.model = "Required";
  const vin = values.vin.trim().toUpperCase();
  if (edited("vin") && vin && !VIN.test(vin)) errors.vin = "A VIN is 17 letters and numbers (no I, O or Q)";
  if (edited("odometer") && values.odometer.trim() && !WHOLE.test(digitsOnly(values.odometer))) {
    errors.odometer = "Whole miles only";
  }
  // Battery CCA tops out in the low thousands; the cap also keeps it inside the save's whole-number range.
  if (edited("CCA") && values.CCA.trim() && !/^\d{1,5}$/.test(values.CCA.trim())) errors.CCA = "A whole number";
  (["frontRotorsDiscardThicknessInches", "rearRotorsDiscardThicknessInches", "oilCapacityQuarts"] as const).forEach(
    (field) => {
      if (edited(field) && values[field].trim() && !DECIMAL.test(values[field].trim())) {
        errors[field] = "A number, like 0.87";
      }
    }
  );
  return errors;
};

const orNull = (value: string) => value.trim() || null;
const numberOrNull = (value: string) => (value.trim() ? Number(value.trim()) : null);

// The upsert replaces the technical info wholesale, so every field goes back, edited or not.
export const toVehicleInput = (values: EditVehicleValues, vehicle: VehicleType): VehicleInput => ({
  id: vehicle.id,
  contactId: vehicle.contactId as string,
  year: values.year.trim(),
  make: values.make.trim(),
  model: values.model.trim(),
  subModel: orNull(values.subModel),
  vin: orNull(values.vin.toUpperCase()),
  licensePlate: orNull(values.licensePlate.toUpperCase()),
  licensePlateState: orNull(values.licensePlateState),
  // Unchanged, the stored text goes back as it was, so a save never rewrites the mileage or its history.
  odometer:
    values.odometer === text(vehicle.odometer)
      ? vehicle.odometer ?? null
      : values.odometer.trim()
      ? digitsOnly(values.odometer)
      : null,
  CCA: numberOrNull(values.CCA),
  notes: orNull(values.notes),
  // Not on this form, but sent back so the save keeps them.
  name: vehicle.name ?? null,
  inactive: vehicle.inactive ?? null,
  hasDrumBrakes: values.rearDrumBrakes === RearDrumBrakes.NOT_SET ? null : values.rearDrumBrakes === RearDrumBrakes.YES,
  parkingBrake: orNull(values.parkingBrake),
  frontRotorsDiscardThicknessInches: numberOrNull(values.frontRotorsDiscardThicknessInches),
  rearRotorsDiscardThicknessInches: numberOrNull(values.rearRotorsDiscardThicknessInches),
  wheelTorque: orNull(values.wheelTorque),
  oilCapacityQuarts: numberOrNull(values.oilCapacityQuarts),
});

type YearMakeModel = Pick<VehicleType, "year" | "make" | "model">;

// The record's own year, make and model, else the job's that the header shows, as the edit form opens on.
const yearMakeModel = (vehicle: VehicleType, shown?: YearMakeModel | null): YearMakeModel => ({
  year: vehicle.year?.trim() || shown?.year,
  make: vehicle.make?.trim() || shown?.make,
  model: vehicle.model?.trim() || shown?.model,
});

// upsertVehicle refuses a save without a customer, year, make and model.
export const canSaveMileageOnly = (vehicle: VehicleType, shown?: YearMakeModel | null): boolean => {
  const { year, make, model } = yearMakeModel(vehicle, shown);
  return !!vehicle.contactId && [year, make, model].every((value) => !!value?.trim());
};

/** The same save as the form's, with only the mileage changed (and a missing year, make or model as shown). */
export const mileageUpdateInput = (vehicle: VehicleType, miles: number, shown?: YearMakeModel | null): VehicleInput =>
  toVehicleInput(
    { ...toFormValues({ ...vehicle, ...yearMakeModel(vehicle, shown) }), odometer: String(miles) },
    vehicle
  );

/** The same save as the form's, with only the notes changed (and a missing year, make or model as shown). */
export const notesUpdateInput = (vehicle: VehicleType, notes: string, shown?: YearMakeModel | null): VehicleInput =>
  toVehicleInput({ ...toFormValues({ ...vehicle, ...yearMakeModel(vehicle, shown) }), notes }, vehicle);

/** The record's notes as the form shows them: older forms saved "null" for none. */
export const vehicleNotes = (vehicle?: VehicleType | null): string | null =>
  vehicle ? toFormValues(vehicle).notes || null : null;
